<template>
    <BaseWidget v-bind="$attrs" :widget="meta" :loading="loading">
        <template #actions>
            <el-select :model-value="filter.status" size="small" class="dw-wo-select" @pointerdown.stop @click.stop :placeholder="$t('common.dashboard.widgetLabels.woRecentStatusPlaceholder')" @change="onStatusChange">
                <el-option :label="$t('common.dashboard.widgetOptions.woStatusAll')" :value="0" />
                <el-option v-for="s in statuses" :key="s.id" :label="$t(s.labelKey)" :value="s.id" />
            </el-select>
            <el-input-number :model-value="filter.limit" size="small" :min="5" :max="20" :step="5" step-strictly class="dw-wo-limit" @pointerdown.stop @click.stop @change="onLimitChange" />
        </template>
        <div class="dw-wo-recent">
            <div v-if="filtered.length" class="dw-wo-list">
                <div v-for="wo in filtered" :key="wo.id" class="dw-wo-item" @click="goTo(wo)">
                    <div class="dw-wo-item-left">
                        <span class="dw-wo-item-customer">{{ wo.customer_name || '—' }}</span>
                        <span class="dw-wo-item-plate">{{ subtitle(wo) }}</span>
                    </div>
                    <div class="dw-wo-item-right">
                        <span class="dw-wo-item-amount">{{ formatPrice(wo.total_price) }}</span>
                        <span class="dw-wo-status-badge" :style="badgeStyle(wo.status?.id)">{{ wo.status?.label }}</span>
                    </div>
                </div>
            </div>
            <div v-else class="dw-wo-empty">
                <div class="dw-wo-empty-icon"><i class="bi bi-clipboard-x"></i></div>
                <div class="dw-wo-empty-text">{{ $t('common.dashboard.widgetEmpty.workOrderRecentTitle') }}</div>
                <div class="dw-wo-empty-sub">{{ $t('common.dashboard.widgetEmpty.workOrderRecentSub') }}</div>
            </div>
        </div>
    </BaseWidget>
</template>

<script>
import BaseWidget from '../BaseWidget.vue';
import widgetMixin from '../widgetMixin';
import dashboardService from '../dashboardService';

export const meta = {
    id: 'work_order_recent',
    titleKey: 'common.dashboard.widgetTitles.work_order_recent',
    icon: 'bi-clipboard-check',
    accent: 'var(--bs-info)',
    accentBg: 'rgba(var(--bs-info-rgb, 114, 57, 234), 0.12)',
    glassRgb: '114, 57, 234',
    defaultCol: 2,
    defaultRow: 1,
    multiInstance: true,
    source: { type: 'module', moduleCode: 'VEHICLE_WORK_ORDER', code: 'ORDER_LIST' },
    defaultConfig: { status_id: 0, limit: 5 },
    configFields: [
        { key: 'status_id', label: 'common.dashboard.widgetFields.woStatus', type: 'select', options: [
            { value: 0, label: 'common.dashboard.widgetOptions.woStatusAll' },
            { value: 1, label: 'common.dashboard.widgetOptions.woStatusOpen' },
            { value: 2, label: 'common.dashboard.widgetOptions.woStatusInProgress' },
            { value: 3, label: 'common.dashboard.widgetOptions.woStatusDone' },
            { value: 4, label: 'common.dashboard.widgetOptions.woStatusSupply' },
            { value: 5, label: 'common.dashboard.widgetOptions.woStatusCanceled' },
            { value: 6, label: 'common.dashboard.widgetOptions.woStatusReturned' },
        ] },
        { key: 'limit', label: 'common.dashboard.widgetFields.woLimit', type: 'number', min: 5, max: 20, step: 5 },
    ],
    buildParams(config) {
        const params = { limit: config?.limit || 5, sort: 'created_at:desc' };
        if (config?.status_id) params.status_id = config.status_id;
        return params;
    },
};

const STATUS_STYLE = {
    1: { color: 'var(--bs-warning)', bg: 'rgba(var(--bs-warning-rgb, 255, 199, 0), 0.12)' },
    2: { color: 'var(--bs-info)', bg: 'rgba(var(--bs-info-rgb, 114, 57, 234), 0.12)' },
    3: { color: 'var(--bs-success)', bg: 'rgba(var(--bs-success-rgb, 80, 205, 137), 0.12)' },
    4: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)' },
    5: { color: 'var(--bs-danger)', bg: 'rgba(var(--bs-danger-rgb, 241, 65, 108), 0.12)' },
    6: { color: 'var(--bs-gray-600)', bg: 'rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.12)' },
};

export default {
    name: 'WorkOrderRecentWidget',
    components: { BaseWidget },
    mixins: [widgetMixin],
    data() {
        return {
            meta,
            statuses: [
                { id: 1, labelKey: 'common.dashboard.widgetOptions.woStatusOpen' },
                { id: 2, labelKey: 'common.dashboard.widgetOptions.woStatusInProgress' },
                { id: 3, labelKey: 'common.dashboard.widgetOptions.woStatusDone' },
                { id: 4, labelKey: 'common.dashboard.widgetOptions.woStatusSupply' },
                { id: 5, labelKey: 'common.dashboard.widgetOptions.woStatusCanceled' },
                { id: 6, labelKey: 'common.dashboard.widgetOptions.woStatusReturned' },
            ],
        };
    },
    computed: {
        filter() {
            return {
                status: this.config.status_id || 0,
                limit: this.config.limit || 5,
            };
        },
        items() {
            return this.apiData?.items || [];
        },
        filtered() {
            return this.items;
        },
    },
    methods: {
        onStatusChange(val) {
            dashboardService.updateInstanceConfig(this.instanceId, { status_id: val });
        },
        onLimitChange(val) {
            const rounded = Math.max(5, Math.min(20, Math.round((val || 5) / 5) * 5));
            dashboardService.updateInstanceConfig(this.instanceId, { limit: rounded });
        },
        goTo(wo) {
            if (!wo?.id) return;
            this.$router.push(`/module/vehicle-work-order/save/${wo.id}`);
        },
        formatPrice(p) {
            const n = parseFloat(p) || 0;
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 });
        },
        subtitle(wo) {
            const parts = [];
            const plate = wo.vehicle_plate_formatted || wo.vehicle_plate;
            if (plate) parts.push(plate);
            if (wo.type_name) parts.push(wo.type_name);
            return parts.join(' - ');
        },
        badgeStyle(statusId) {
            const s = STATUS_STYLE[statusId];
            if (!s) return { color: 'var(--bs-gray-500)', background: 'rgba(var(--bs-secondary-rgb, 108, 117, 125), 0.10)' };
            return { color: s.color, background: s.bg };
        },
    },
};
</script>

<style>
.dw-wo-empty {
    flex: 1;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 8px; text-align: center;
    padding: 8px 12px;
}
.dw-wo-empty-icon {
    width: 56px; height: 56px;
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(var(--bs-info-rgb, 114, 57, 234), 0.10);
    color: var(--bs-info);
    margin-bottom: 4px;
}
.dw-wo-empty-icon .bi { font-size: 26px; }
.dw-wo-empty-text {
    font-size: 13px; font-weight: 600;
    color: var(--bs-body-color);
}
.dw-wo-empty-sub {
    font-size: 11px; color: var(--bs-gray-500);
    line-height: 1.4; max-width: 220px;
}
</style>
